import axios, { AxiosProgressEvent, AxiosResponse } from "axios";
import { IAxiosBlankReturn, IAxiosReturn } from "../interface";
import { processAxiosError } from "../processAxiosError";
import {
  TLF_BUNDLE_CHUNK_SIZE_BYTES,
  TlfBundleAnalysisResponse,
  TlfBundleAnalyzeRequest,
  TlfBundleCompleteUploadRequest,
  TlfBundleCompleteUploadResponse,
  TlfBundleConflictDiff,
  TlfBundleConflictDiffRequest,
  TlfBundleConflictPageRequest,
  TlfBundleConflictPageResponse,
  TlfBundleDeleteExportRequest,
  TlfBundleExportChunkRequest,
  TlfBundleExportRequest,
  TlfBundleImportAcceptedResponse,
  TlfBundleImportFailurePage,
  TlfBundleImportFailuresRequest,
  TlfBundleImportJobRequest,
  TlfBundleImportJobStatus,
  TlfBundleImportRequest,
  TlfBundleUploadChunkRequest,
} from "./interface";
import { retryWithBackoff } from "./retryWithBackoff";

/**
 * Build the route prefix for TLF bundle operations on one hierarchy.
 *
 * @param backEndUrl - Backend base URL
 * @param hierarchyId - Hierarchy identifier to encode into the route
 * @returns TLF bundle route prefix
 */
const hierarchyUrl = (backEndUrl: string, hierarchyId: string): string =>
  `${backEndUrl.replace(/\/+$/, "")}/Hierarchies/${encodeURIComponent(hierarchyId)}/TlfBundle`;

/**
 * Run a response-producing request and normalise Axios failures.
 *
 * @param message - Error prefix returned when the request fails
 * @param request - Axios request that returns response data
 * @returns Standardised success or failure response
 */
const requestData = async <T>(
  message: string,
  request: () => Promise<AxiosResponse<T>>,
): Promise<IAxiosReturn<T>> => {
  try {
    const response = await request();
    return { success: true, data: response.data };
  } catch (error: unknown) {
    if (axios.isAxiosError<unknown>(error) && error.response?.data instanceof Blob) {
      try {
        const text = await new Response(error.response.data).text();
        let data: unknown;
        try {
          data = JSON.parse(text);
        } catch {
          // Some endpoints return plain-text error details rather than JSON.
          data = text;
        }
        error.response.data = typeof data === "string" ? { message: data } : data;
      } catch {
        // Preserve the original error if its response body cannot be read.
      }
    }
    return processAxiosError(message, error);
  }
};

/**
 * Run a no-content request and normalise Axios failures.
 *
 * @param message - Error prefix returned when the request fails
 * @param request - Axios request that has no response body
 * @returns Standardised success or failure response
 */
const requestBlank = async (
  message: string,
  request: () => Promise<unknown>,
): Promise<IAxiosBlankReturn> => {
  try {
    await request();
    return { success: true };
  } catch (error: unknown) {
    return processAxiosError(message, error);
  }
};

/**
 * Create and download a complete compressed TLF bundle file.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and selected items to export
 * @param controller - Request cancellation controller
 * @param onProgress - Optional callback with downloaded and total byte counts
 * @returns Compressed bundle file or a standardised failure
 */
const createExport = async (
  backEndUrl: string,
  params: TlfBundleExportRequest,
  controller: AbortController,
  onProgress?: (transferredBytes: number, totalBytes: number) => void,
): Promise<IAxiosReturn<Blob>> => {
  const { hierarchyId, ...body } = params;
  return requestData("Create TLF bundle export failed", () =>
    axios.post<Blob>(`${hierarchyUrl(backEndUrl, hierarchyId)}/Export`, body, {
      signal: controller.signal,
      responseType: "blob",
      onDownloadProgress: (event: AxiosProgressEvent) => {
        onProgress?.(event.loaded, event.total ?? 0);
      },
    }),
  );
};

/**
 * Download one export chunk with bounded retries for transient failures.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Export and chunk identifiers
 * @param controller - Request cancellation controller
 * @returns Binary chunk data or a standardised failure
 */
const downloadExportChunk = async (
  backEndUrl: string,
  params: TlfBundleExportChunkRequest,
  controller: AbortController,
): Promise<IAxiosReturn<Uint8Array>> => {
  const url = `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Export/${encodeURIComponent(params.exportId)}/Chunk/${params.sequenceNumber}`;
  try {
    const response = await retryWithBackoff(
      () => axios.get<ArrayBuffer>(url, { responseType: "arraybuffer", signal: controller.signal }),
      controller.signal,
    );
    return { success: true, data: new Uint8Array(response.data) };
  } catch (error: unknown) {
    return processAxiosError("Download TLF bundle chunk failed", error);
  }
};

/**
 * Delete a prepared export after its chunks have been consumed.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and export identifiers
 * @param controller - Request cancellation controller
 * @returns Standardised deletion result
 */
const deleteExport = async (
  backEndUrl: string,
  params: TlfBundleDeleteExportRequest,
  controller: AbortController,
): Promise<IAxiosBlankReturn> =>
  requestBlank("Delete TLF bundle export failed", () =>
    axios.delete(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Export/${encodeURIComponent(params.exportId)}`,
      { signal: controller.signal },
    ),
  );

/**
 * Upload one compressed bundle chunk and report its request progress.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Upload metadata and chunk bytes
 * @param controller - Request cancellation controller
 * @param onProgress - Optional request progress callback
 * @returns Standardised upload result
 */
const uploadChunk = async (
  backEndUrl: string,
  params: TlfBundleUploadChunkRequest,
  controller: AbortController,
  onProgress?: (progressPercent: number) => void,
): Promise<IAxiosBlankReturn> => {
  if (params.chunk.size > TLF_BUNDLE_CHUNK_SIZE_BYTES) {
    return { success: false, errorText: "TLF bundle chunks must not exceed 2 MB" };
  }

  const formData = new FormData();
  formData.append("uploadId", params.uploadId);
  formData.append("sequenceNumber", params.sequenceNumber.toString());
  formData.append("totalChunks", params.totalChunks.toString());
  formData.append("chunk", params.chunk, `chunk_${params.sequenceNumber}.tlfbundle.gz`);
  const config = {
    signal: controller.signal,
    ...(onProgress
      ? {
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
          },
        }
      : {}),
  };

  try {
    await retryWithBackoff(
      () =>
        axios.post(
          `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Upload/Chunk`,
          formData,
          config,
        ),
      controller.signal,
    );
    return { success: true };
  } catch (error: unknown) {
    return processAxiosError("Upload TLF bundle chunk failed", error);
  }
};

/**
 * Finalise a chunked bundle upload and receive its content metadata.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Upload identifiers, byte count, and digest
 * @param controller - Request cancellation controller
 * @returns Completed upload metadata or a standardised failure
 */
const completeUpload = async (
  backEndUrl: string,
  params: TlfBundleCompleteUploadRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleCompleteUploadResponse>> => {
  const { hierarchyId, ...body } = params;
  return requestData("Complete TLF bundle upload failed", () =>
    axios.post<TlfBundleCompleteUploadResponse>(
      `${hierarchyUrl(backEndUrl, hierarchyId)}/Upload/Complete`,
      body,
      { signal: controller.signal },
    ),
  );
};

/**
 * Analyse an uploaded bundle against the target hierarchy.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and upload identifiers
 * @param controller - Request cancellation controller
 * @returns Bundle analysis summary or a standardised failure
 */
const analyzeUpload = async (
  backEndUrl: string,
  params: TlfBundleAnalyzeRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleAnalysisResponse>> =>
  requestData("Analyse TLF bundle failed", () =>
    axios.post<TlfBundleAnalysisResponse>(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Analyze`,
      { uploadId: params.uploadId },
      { signal: controller.signal },
    ),
  );

/**
 * Load one page of conflicts for an analysis.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy, analysis, and page identifiers
 * @param controller - Request cancellation controller
 * @returns Conflict page or a standardised failure
 */
const listConflicts = async (
  backEndUrl: string,
  params: TlfBundleConflictPageRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleConflictPageResponse>> => {
  const query = new URLSearchParams({
    pageNumber: params.pageNumber.toString(),
    pageSize: params.pageSize.toString(),
  });
  return requestData("Load TLF bundle conflicts failed", () =>
    axios.get<TlfBundleConflictPageResponse>(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Analysis/${encodeURIComponent(params.analysisId)}/Conflicts?${query.toString()}`,
      { signal: controller.signal },
    ),
  );
};

/**
 * Load field-level differences for one conflict.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy, analysis, and conflict identifiers
 * @param controller - Request cancellation controller
 * @returns Field differences or a standardised failure
 */
const getConflictDiff = async (
  backEndUrl: string,
  params: TlfBundleConflictDiffRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleConflictDiff[]>> =>
  requestData("Load TLF bundle conflict differences failed", () =>
    axios.get<TlfBundleConflictDiff[]>(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Analysis/${encodeURIComponent(params.analysisId)}/Conflicts/${encodeURIComponent(params.conflictId)}/Diff`,
      { signal: controller.signal },
    ),
  );

/**
 * Start an import using an analysed upload and its conflict resolutions.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy, upload, analysis, and resolution identifiers
 * @param controller - Request cancellation controller
 * @returns Accepted import job identifier or a standardised failure
 */
const startImport = async (
  backEndUrl: string,
  params: TlfBundleImportRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleImportAcceptedResponse>> => {
  const { hierarchyId, ...body } = params;
  return requestData("Start TLF bundle import failed", () =>
    axios.post<TlfBundleImportAcceptedResponse>(
      `${hierarchyUrl(backEndUrl, hierarchyId)}/Import`,
      body,
      { signal: controller.signal },
    ),
  );
};

/**
 * Load the current state of a background import job.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and job identifiers
 * @param controller - Request cancellation controller
 * @returns Import job status or a standardised failure
 */
const getImportStatus = async (
  backEndUrl: string,
  params: TlfBundleImportJobRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleImportJobStatus>> =>
  requestData("Load TLF bundle import status failed", () =>
    axios.get<TlfBundleImportJobStatus>(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Import/${encodeURIComponent(params.jobId)}`,
      { signal: controller.signal },
    ),
  );

/**
 * Cancel a queued or running background import job.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and job identifiers
 * @param controller - Request cancellation controller
 * @returns Standardised cancellation result
 */
const cancelImport = async (
  backEndUrl: string,
  params: TlfBundleImportJobRequest,
  controller: AbortController,
): Promise<IAxiosBlankReturn> =>
  requestBlank("Cancel TLF bundle import failed", () =>
    axios.post(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Import/${encodeURIComponent(params.jobId)}/Cancel`,
      undefined,
      { signal: controller.signal },
    ),
  );

/**
 * Load a page of item failures for a background import job.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy, job, and paging identifiers
 * @param controller - Request cancellation controller
 * @returns Failure page or a standardised failure
 */
const listImportFailures = async (
  backEndUrl: string,
  params: TlfBundleImportFailuresRequest,
  controller: AbortController,
): Promise<IAxiosReturn<TlfBundleImportFailurePage>> => {
  const query = new URLSearchParams({ skip: params.skip.toString(), take: params.take.toString() });
  return requestData("Load TLF bundle import failures failed", () =>
    axios.get<TlfBundleImportFailurePage>(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Import/${encodeURIComponent(params.jobId)}/Failures?${query.toString()}`,
      { signal: controller.signal },
    ),
  );
};

/**
 * Retry the failed items from an eligible import job.
 *
 * @param backEndUrl - Backend base URL
 * @param params - Hierarchy and job identifiers
 * @param controller - Request cancellation controller
 * @returns Standardised retry result
 */
const retryFailedImport = async (
  backEndUrl: string,
  params: TlfBundleImportJobRequest,
  controller: AbortController,
): Promise<IAxiosBlankReturn> =>
  requestBlank("Retry failed TLF bundle import items failed", () =>
    axios.post(
      `${hierarchyUrl(backEndUrl, params.hierarchyId)}/Import/${encodeURIComponent(params.jobId)}/RetryFailed`,
      undefined,
      { signal: controller.signal },
    ),
  );

/**
 * API methods for the currently implemented TLF bundle endpoints.
 */
export const tlfBundleApi = {
  analyzeUpload,
  completeUpload,
  createExport,
  deleteExport,
  downloadExportChunk,
  getConflictDiff,
  getImportStatus,
  listConflicts,
  listImportFailures,
  cancelImport,
  retryFailedImport,
  startImport,
  uploadChunk,
};
